import type { ClassificationLevel } from "@esi/shared";
import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  choiceConsequence,
  impactContext,
  impactIcon,
  levelImpact,
  levelSummary,
  type Sentence,
} from "./impact.ts";
import { InPractice, LevelHeader, TOUCH_ICON } from "./LevelList.tsx";
import type { Usage } from "./review.ts";

/**
 * The classification levels where they cannot be changed (openspec change classification-editor,
 * D9): the backoffice at an entity level or under a support grant, and a governed project. The
 * editor's rows and wording without its controls: each row opens to its choices as text and the
 * same "In practice" list. `only` keeps the levels a governed project uses (in the scheme's
 * order; an id the scheme no longer has is listed by its id, without wording).
 */
export function ReadOnlyLevels(props: {
  /** The whole scheme: positions, the private level and the wording read it. */
  scheme: readonly ClassificationLevel[];
  /** The effective settings the wording reads (outbound policy, declassification, default). */
  settings: readonly { key: string; value: unknown }[];
  /** Files per level, when the viewer may read them. */
  usage?: Usage;
  only?: readonly string[];
  label: string;
}) {
  const { t } = useTranslation();
  // Keys computed from the level (impact.ts): typed as plain strings.
  const translateKey = t as unknown as (key: string, values?: object) => string;
  const say = (s: Sentence) => translateKey(`adminSettings.levels.${s.key}`, s.values);
  const id = useId();
  const [open, setOpen] = useState<string | null>(null);
  const context = impactContext(props.scheme, props.settings);
  const shown = props.only
    ? props.scheme.filter((level) => props.only?.includes(level.id))
    : props.scheme;
  const unknown = (props.only ?? []).filter((level) => !props.scheme.some((l) => l.id === level));
  return (
    <ol aria-label={props.label} className="divide-y divide-line rounded-md border border-line">
      {shown.map((level) => {
        const position = props.scheme.indexOf(level) + 1;
        const files = props.usage?.[level.id]?.documents ?? 0;
        const panel = `${id}-${level.id}`;
        const isOpen = open === level.id;
        const egress = level.egressFloor ?? "readers";
        return (
          <li key={level.id}>
            <LevelHeader
              position={position}
              name={level.label}
              summary={levelSummary(level, context).map(say)}
              files={files}
            >
              <Button
                type="button"
                size="icon-xs"
                variant="ghost"
                className={cn("shrink-0", TOUCH_ICON)}
                aria-label={t("adminSettings.levels.details", { level: level.label })}
                aria-expanded={isOpen}
                aria-controls={panel}
                onClick={() => setOpen(isOpen ? null : level.id)}
              >
                <ChevronDown
                  aria-hidden
                  className={cn("transition-transform", isOpen && "rotate-180")}
                />
              </Button>
            </LevelHeader>
            {isOpen ? (
              <div
                id={panel}
                className="grid gap-5 border-t border-line bg-surface-sunken p-4 @xl:grid-cols-2"
              >
                <dl className="flex min-w-0 flex-col gap-3 text-sm">
                  {(
                    [
                      [
                        t("adminSettings.levels.fields.readers"),
                        t(`adminSettings.levels.readersOption.${level.readersFloor}`),
                      ],
                      [
                        t("adminSettings.levels.fields.download"),
                        say(choiceConsequence("download", level, context)),
                      ],
                      [t("adminSettings.egress.title"), t(`adminSettings.egress.${egress}`)],
                    ] as const
                  ).map(([term, value]) => (
                    <div key={term} className="flex flex-col gap-0.5">
                      <dt className="font-medium text-fg">{term}</dt>
                      <dd className="text-fg-secondary">{value}</dd>
                    </div>
                  ))}
                </dl>
                <InPractice
                  id={`${panel}-practice`}
                  impact={levelImpact(level, context).map((s) => ({
                    icon: impactIcon(s),
                    text: say(s),
                  }))}
                />
              </div>
            ) : null}
          </li>
        );
      })}
      {unknown.map((level) => (
        <li key={level} className="flex min-h-12 items-center gap-3 px-3 py-1.5">
          <span className="w-4 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-medium break-words text-fg sm:min-w-0 sm:truncate">
              {level}
            </span>
            <span className="text-xs text-fg-muted">{t("adminSettings.levels.notInScheme")}</span>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * "What these levels mean": the read-only levels behind one disclosure, in the backoffice row
 * and on a governed project's Classifications line (D9). A `@container`, so an open level lays
 * out like the editor's (two columns from `@xl`).
 */
export function LevelsDisclosure(props: {
  scheme: readonly ClassificationLevel[];
  settings: readonly { key: string; value: unknown }[];
  usage?: Usage;
  only?: readonly string[];
  label: string;
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="@container flex w-full flex-col items-start gap-2">
      <Button
        type="button"
        size="xs"
        variant="ghost"
        className="-ml-2 max-sm:h-11"
        aria-expanded={props.open}
        aria-controls={id}
        onClick={props.onToggle}
      >
        <ChevronDown
          aria-hidden
          className={cn("transition-transform", props.open && "rotate-180")}
        />
        {t("adminSettings.levels.whatTheyMean")}
      </Button>
      {props.open ? (
        <div id={id} className="w-full">
          <ReadOnlyLevels
            scheme={props.scheme}
            settings={props.settings}
            usage={props.usage}
            only={props.only}
            label={props.label}
          />
        </div>
      ) : null}
    </div>
  );
}
